import { AsyncCheckoutOrderType } from "@/components/Checkout/types";

import {
  brandclub_colors,
  Button,
  formatMoney,
  getRetailerFromId,
} from "@brandclub/common-ui";

import { styled } from "@mui/material/styles";
import { useCheckoutUI } from "../../../CheckoutUIProvider";
import { useCheckoutCartInfo } from "../../../hooks/useCheckoutCartInfo";

import {
  saveRetailerCredentials,
  updateOrderType,
} from "@/api/rest/checkoutApi";
import { useCheckoutRetailer } from "../../../hooks/useCheckoutRetailer";
import EditConnectPopup from "../retailerConnect/EditConnectPopup";
import { useInjectRetailerCredentials } from "./RetailerConnect";
import { useEffect, useState } from "react";

import CheckRoundedIcon from "@mui/icons-material/CheckRounded";

const CheckoutOptionsContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: 18,
}));

const getUserAccountDisplayText = ({
  rewardAmount,
  connectRewardAmount,
  estimatedGuestOrderTotal,
  estimatedUserAccountOrderTotal,
  estimatedUserAccountOrderShippingFees,
}: {
  rewardAmount: number;
  connectRewardAmount: number;
  estimatedGuestOrderTotal: number;
  estimatedUserAccountOrderTotal: number;
  estimatedUserAccountOrderShippingFees: number;
}) => {
  let line1Text = "";
  let line1Highlight = "";
  let line2Text = "";
  let line2Highlight = "";
  if (rewardAmount) {
    line1Text = `Earn ${formatMoney(rewardAmount)} in cash rewards`;
  } else {
    line1Text = `Earn cash rewards and member-only exclusives`;
  }

  if (connectRewardAmount) {
    line1Highlight = `get ${formatMoney(connectRewardAmount)} in bonus rewards`;
  }

  if (!estimatedUserAccountOrderShippingFees) {
    line2Text = `Get fast, free shipping`;
  } else {
    line2Text = `Pay ${formatMoney(
      estimatedUserAccountOrderShippingFees
    )} in estimated shipping and service fees`;
  }

  if (estimatedGuestOrderTotal > estimatedUserAccountOrderTotal) {
    line2Highlight = `get ${formatMoney(
      estimatedGuestOrderTotal - estimatedUserAccountOrderTotal
    )} in estimated savings`;
  }

  return {
    line1Text,
    line1Highlight,
    line2Text,
    line2Highlight,
  };
};

const getGuestDisplayText = ({
  rewardAmount,
  connectRewardAmount,
  estimatedGuestOrderTotal,
  estimatedUserAccountOrderTotal,
  estimatedGuestOrderShippingFees,
  estimatedGuestServiceFees,
}: {
  rewardAmount: number;
  connectRewardAmount: number;
  estimatedGuestOrderTotal: number;
  estimatedUserAccountOrderTotal: number;
  estimatedGuestOrderShippingFees: number;
  estimatedGuestServiceFees: number;
}) => {
  let line1Text = "";
  let line1Highlight = "";
  let line2Text = "";
  let line2Highlight = "";

  if (rewardAmount) {
    line1Text = `Earn ${formatMoney(
      rewardAmount - (connectRewardAmount ?? 0)
    )} in cash rewards`;
  } else {
    line1Text = "";
  }

  if (estimatedGuestOrderTotal > estimatedUserAccountOrderTotal) {
    line2Text = `Pay ${formatMoney(
      estimatedGuestOrderShippingFees + estimatedGuestServiceFees
    )} in estimated shipping and service fees`;
  }

  return {
    line1Text,
    line1Highlight,
    line2Text,
    line2Highlight,
  };
};

const AccountOptionContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: 12,

  border: `1px solid #EAB74B`,
  backgroundColor: "#FFFCEC",
  padding: 20,
  borderRadius: 8,
  color: theme.vars.palette.text.primary,
  overflow: "hidden",
  ".desktop": {
    display: "flex",
    [theme.breakpoints.down("md")]: {
      display: "none",
    },
  },
  ".mobile": {
    display: "none",
    [theme.breakpoints.down("md")]: {
      display: "flex",
    },
  },

  ".account-option-head": {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
  },

  ".account-option-head-title": {
    fontSize: 20,
    flex: 1,
    fontWeight: 600,
    marginBottom: 3,
    [theme.breakpoints.down("md")]: {
      fontSize: 14,
    },
  },

  ".option-head-checkbox": {
    width: 20,
    marginTop: 4,
    cursor: "pointer",
    height: 20,
    borderRadius: 999,
    border: `1px solid ${brandclub_colors.lightGray}`,
    backgroundColor: brandclub_colors.white,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    [theme.breakpoints.down("md")]: {
      marginTop: 2,
      width: 16,
      height: 16,
    },

    " .option-head-checkbox-selected": {
      backgroundColor: theme.vars.palette.text.primary,
      width: 10,
      height: 10,
      borderRadius: 999,
      [theme.breakpoints.down("md")]: {
        width: 8,
        height: 8,
      },
    },
  },
  ".account-option-body": {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    padding: "0px 30px 17px 30px",
    fontSize: 14,
    [theme.breakpoints.down("md")]: {
      padding: "0px 20px 0px 20px",
      fontSize: 12,
    },
    ".account-option-body-line1": {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      "&.username": {
        fontWeight: 600,
      },
    },
    ".account-option-body-line2": {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
    },
  },

  ".account-option-button": {
    marginTop: 10,
    width: "100%",

    justifyContent: "center",
  },
  ".edit-button": {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
}));

const Ribbon = styled("div")(({ theme }) => ({
  fontSize: 10,
  fontWeight: 600,
  color: "#fff",
  "--r": ".8em",
  paddingInline: "calc(var(--r) + .3em)",
  lineHeight: 1.8,
  clipPath:
    "polygon(0 0,100% 0,calc(100% - var(--r)) 50%,100% 100%,0 100%, var(--r) 50%)",
  // background: "#CC333F",
  width: "fit-content",
}));

const AccountOption = ({
  selected = false,
  onClick,
  type,
}: {
  selected: boolean;
  onClick: () => void;
  type: AsyncCheckoutOrderType;
}) => {
  const { currentRetailer } = useCheckoutRetailer();
  const [state, send] = useCheckoutUI();
  const { rewardAmounts } = useCheckoutCartInfo();
  const { checkoutIntent } = state.context;

  const [currentUsername, setCurrentUsername] = useState<string>("");
  const [currentPassword, setCurrentPassword] = useState<string>("");

  const {
    username: existingUsername,
    password: existingPassword,
    loading,
  } = useInjectRetailerCredentials();

  useEffect(() => {
    // Set the default password to the decrypted password if it matches the hash
    if (!loading) {
      if (existingUsername) setCurrentUsername(existingUsername);
      if (existingPassword) setCurrentPassword(existingPassword);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingPassword, existingUsername, loading]);

  const hasRetailerConnection = !!checkoutIntent?.retailerConnection?.username;

  const { line1Text, line1Highlight, line2Text, line2Highlight } =
    getUserAccountDisplayText({
      rewardAmount: rewardAmounts.totalRewardAmount,
      connectRewardAmount: rewardAmounts.retailerConnectRewardAmount,
      estimatedGuestOrderTotal:
        checkoutIntent?.estimatedGuestOrderSummary?.total ?? 0,
      estimatedUserAccountOrderTotal:
        checkoutIntent?.estimatedUserAccountOrderSummary?.total ?? 0,
      estimatedUserAccountOrderShippingFees:
        checkoutIntent?.estimatedUserAccountOrderSummary?.shippingFees ?? 0,
    });

  const onSaveCredentials = async (username: string, password: string) => {
    setCurrentUsername(username);
    setCurrentPassword(password);
    try {
      const checkoutIntent = await saveRetailerCredentials({
        retailerId: currentRetailer?.retailerId?.toString() ?? "",
        checkoutIntentId: state.context.checkoutIntent?.checkoutIntentId!,
        credentials: {
          username,
          password,
        },
      });
      send({
        type: "SET_CHECKOUT_INTENT",
        data: { checkoutIntent },
      });
    } catch (error) {
      console.error(error);
    }
  };

  const openEdit = () => {
    EditConnectPopup.show({
      connectReward: rewardAmounts?.retailerConnectRewardAmount,
      onSaveCredentials: onSaveCredentials,
      currentUsername: currentUsername,
      currentPassword: currentPassword,
      retailer: currentRetailer,

      checkoutIntentUsername: checkoutIntent?.retailerConnection?.username,
    });
  };

  const title = hasRetailerConnection
    ? `Use my ${
        currentRetailer?.retailerId
          ? getRetailerFromId(currentRetailer?.retailerId)
          : "Retailer"
      } account`
    : `Checkout with ${
        currentRetailer?.retailerId
          ? getRetailerFromId(currentRetailer?.retailerId)
          : "Retailer"
      }`;
  return (
    <AccountOptionContainer>
      <div className="account-option-head">
        <div className="option-head-checkbox" onClick={onClick} role="button">
          {selected && <div className="option-head-checkbox-selected"></div>}
        </div>
        <div style={{ flex: 1 }}>
          <div className="account-option-head-title">{title}</div>
          <Ribbon sx={{ backgroundColor: "#207D3B" }}>Best Value</Ribbon>
        </div>
        {hasRetailerConnection ? (
          <div className="edit-button">
            <Button
              variant={"outline"}
              onClick={openEdit}
              disableMaxWidth
              height={35}
              sx={(theme) => ({
                width: "100%",
                color: theme.vars.palette.text.primary,

                borderColor: theme.vars.palette.text.primary,

                backgroundColor: "#FFF",
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 600,
                padding: "0px 15px",
                ":hover": {
                  backgroundColor: "#FFF",
                },
              })}
            >
              Edit
            </Button>
          </div>
        ) : (
          <div className="account-option-head-button desktop">
            <Button
              variant={"solid"}
              onClick={openEdit}
              disableMaxWidth
              sx={{
                width: "100%",
                maxWidth: 380,
                backgroundColor: "#207D3B",
                color: "#FFF",
                ":hover": {
                  backgroundColor: "#207D3B",
                },
              }}
            >
              Unlock full rewards access
            </Button>
          </div>
        )}
      </div>

      <div className="account-option-body">
        {hasRetailerConnection ? (
          <div className="account-option-body-line1 username">
            {checkoutIntent?.retailerConnection?.username}
          </div>
        ) : null}
        <div className="account-option-body-line1">
          <CheckRoundedIcon
            sx={{ width: 16, height: 16, color: "#207D3B", marginRight: "5px" }}
          />
          {line1Text}
          {line1Highlight ? (
            <span className="account-option-body-line1-highlight">
              {" "}
              - <strong>{line1Highlight}</strong>
            </span>
          ) : null}
        </div>
        <div className="account-option-body-line2">
          <CheckRoundedIcon
            sx={{
              width: 16,
              height: 16,
              color: "#207D3B",
              marginRight: "5px",
            }}
          />

          {line2Text}
          {line2Highlight ? (
            <span className="account-option-body-line2-highlight">
              {" "}
              - <strong>{line2Highlight}</strong>
            </span>
          ) : null}
        </div>
      </div>

      {hasRetailerConnection ? null : (
        <div className="account-option-button mobile">
          <Button
            variant={"solid"}
            onClick={openEdit}
            disableMaxWidth
            sx={{
              width: "100%",
              maxWidth: 380,
              backgroundColor: "#207D3B",
              color: "#FFF",
              ":hover": {
                backgroundColor: "#207D3B",
              },
            }}
          >
            Unlock full rewards access
          </Button>
        </div>
      )}
    </AccountOptionContainer>
  );
};

const OptionContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  border: `1px solid ${brandclub_colors.lightGray}`,
  borderRadius: 8,
  overflow: "hidden",

  ".mobile": {
    display: "none",
    [theme.breakpoints.down("md")]: {
      display: "flex",
    },
  },
  ".desktop": {
    display: "flex",
    [theme.breakpoints.down("md")]: {
      display: "none",
    },
  },

  ".option-head": {
    display: "flex",
    justifyContent: "flex-start",
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,

    padding: "20px 20px",
    backgroundColor: brandclub_colors.blueWhite,
    borderBottom: `1px solid ${brandclub_colors.lightGray}`,
    [theme.breakpoints.down("md")]: {},

    ".option-head-checkbox": {
      width: 20,
      marginTop: 4,
      cursor: "pointer",
      height: 20,
      borderRadius: 999,
      border: `1px solid ${brandclub_colors.lightGray}`,
      backgroundColor: brandclub_colors.white,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      [theme.breakpoints.down("md")]: {
        marginTop: 2,
        width: 16,
        height: 16,
      },

      " .option-head-checkbox-selected": {
        backgroundColor: theme.vars.palette.text.primary,
        width: 10,
        height: 10,
        borderRadius: 999,
        [theme.breakpoints.down("md")]: {
          width: 8,
          height: 8,
        },
      },
    },
    ".option-head-icon": {
      width: 32,
      height: 32,
      borderRadius: 999,
      objectFit: "cover",
      [theme.breakpoints.down("md")]: {
        width: 20,
        height: 20,
      },
    },
    ".option-head-title": {
      fontSize: 20,
      flex: 1,
      fontWeight: 600,
      marginBottom: 3,
      [theme.breakpoints.down("md")]: {
        fontSize: 14,
      },
    },
  },
  ".option-body": {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    padding: "17px 50px 17px 50px",
    fontSize: 14,
    [theme.breakpoints.down("md")]: {
      padding: "12px 20px 16px 20px",
      fontSize: 12,
    },

    ".option-body-line1": {
      padding: "0px 0px",
      [theme.breakpoints.down("md")]: {
        padding: "0px 15px",
      },
    },
    ".option-body-line2": {
      padding: "0px 0px",
      [theme.breakpoints.down("md")]: {
        padding: "0px 15px",
      },
    },
  },
}));

const GuestOption = ({
  selected = false,
  onClick,
}: {
  selected: boolean;
  onClick: () => void;
}) => {
  const { currentRetailer } = useCheckoutRetailer();

  const [state] = useCheckoutUI();
  const { rewardAmounts } = useCheckoutCartInfo();
  const { checkoutIntent } = state.context;

  const hasRetailerConnection = !!checkoutIntent?.retailerConnection?.username;

  const title = hasRetailerConnection
    ? `I don't want to use my ${
        currentRetailer?.retailerId
          ? getRetailerFromId(currentRetailer?.retailerId)
          : "Retailer"
      } account`
    : `I don't have an ${
        currentRetailer?.retailerId
          ? getRetailerFromId(currentRetailer?.retailerId)
          : "Retailer"
      } account`;

  const { line1Text, line1Highlight, line2Text, line2Highlight } =
    getGuestDisplayText({
      rewardAmount: rewardAmounts.totalRewardAmount,
      connectRewardAmount: rewardAmounts.retailerConnectRewardAmount,
      estimatedGuestOrderTotal:
        checkoutIntent?.estimatedGuestOrderSummary?.total ?? 0,
      estimatedUserAccountOrderTotal:
        checkoutIntent?.estimatedUserAccountOrderSummary?.total ?? 0,
      estimatedGuestOrderShippingFees:
        checkoutIntent?.estimatedGuestOrderSummary?.shippingFees ?? 0,
      estimatedGuestServiceFees:
        checkoutIntent?.estimatedGuestOrderSummary?.serviceFee ?? 0,
    });

  return (
    <OptionContainer>
      <div className="option-head">
        <div className="option-head-checkbox" onClick={onClick} role="button">
          {selected && <div className="option-head-checkbox-selected"></div>}
        </div>
        <div>
          <div className="option-head-title">{title}</div>
          <Ribbon sx={{ backgroundColor: "#CC333F" }}>Guest checkout</Ribbon>
        </div>
      </div>
      <div className="option-body">
        <div className="option-body-line1">
          {line1Text}
          {line1Highlight ? (
            <span className="option-body-line1-highlight">
              {" "}
              - <strong>{line1Highlight}</strong>
            </span>
          ) : null}
        </div>
        <div className="option-body-line2">
          {line2Text}
          {line2Highlight ? (
            <span className="option-body-line1-highlight">
              {" "}
              - <strong>{line2Highlight}</strong>
            </span>
          ) : null}
        </div>
      </div>
    </OptionContainer>
  );
};

const CheckoutOptions = () => {
  const [machineState, send] = useCheckoutUI();

  const { retailerLogin } = machineState.context;

  const toggleOrderType = async (orderType: AsyncCheckoutOrderType) => {
    const { checkoutIntent } = await updateOrderType({
      orderType,
      checkoutIntentId: machineState.context.checkoutIntent?.checkoutIntentId!,
    });
    send({
      type: "SET_CHECKOUT_INTENT",
      data: { checkoutIntent },
    });
  };

  // @ts-ignore
  const { checkoutIntent } = machineState.context;

  const orderType = checkoutIntent?.orderType ?? AsyncCheckoutOrderType.REGULAR;

  const handleOrderTypeChange = (orderType: AsyncCheckoutOrderType) => {
    console.log("orderType", orderType);
    toggleOrderType(orderType);
  };

  return (
    <CheckoutOptionsContainer>
      {retailerLogin.open ? (
        <>
          <AccountOption
            selected={orderType === AsyncCheckoutOrderType.REGULAR}
            onClick={() =>
              handleOrderTypeChange(AsyncCheckoutOrderType.REGULAR)
            }
            type={AsyncCheckoutOrderType.REGULAR}
          />
          <GuestOption
            selected={orderType === AsyncCheckoutOrderType.GUEST}
            onClick={() => handleOrderTypeChange(AsyncCheckoutOrderType.GUEST)}
          />
        </>
      ) : (
        <div>
          {orderType === AsyncCheckoutOrderType.REGULAR ? (
            <AccountOption
              selected={orderType === AsyncCheckoutOrderType.REGULAR}
              onClick={() =>
                handleOrderTypeChange(AsyncCheckoutOrderType.REGULAR)
              }
              type={AsyncCheckoutOrderType.REGULAR}
            />
          ) : (
            <GuestOption
              selected={orderType === AsyncCheckoutOrderType.GUEST}
              onClick={() =>
                handleOrderTypeChange(AsyncCheckoutOrderType.GUEST)
              }
            />
          )}
        </div>
      )}
    </CheckoutOptionsContainer>
  );
};

export default CheckoutOptions;
